import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ShieldCheck, X } from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { formatPYG } from "../../utils/format";
import { normalizeRole } from "../../utils/roles";
import SelectBuscable from "../../components/SelectBuscable";

/* Fix EX (2026-10-05): "En compras, luego de tareas, agregar un botón más de 'Rango p/
   Aprobaciones'. Donde permita agregar condiciones para aprobaciones en 2 puntos durante el
   proceso de compras: en 'Aprobaciones' (...) tantos perfiles se consideren necesarios para la
   aprobación y el paso a Pedido a Proveedores; en 'Análisis de Presupuesto' (...) perfiles de
   aprobación sujetos a rangos de montos. Estos montos deben ser editables" (CEN, 05/10).
   La regla vive en el backend (routers/aprobacion_doble.py); acá se configura y se muestra. */

// ── Cálculo del AP (mismo criterio que el backend) ──────────────────────────
const asignaciones = (it) => (Array.isArray(it.allocations)
  ? it.allocations.filter(a => Number.isInteger(a?.provider_idx) && a.provider_idx >= 0)
  : (Number.isInteger(it.selected_idx) && it.selected_idx >= 0 ? [{ provider_idx: it.selected_idx, quantity: it.quantity ?? 1 }] : []));

// Fix HI (2026-10-21): lo que queda por aprobar para «Cerrar AP» son los proveedores abiertos: los que ya se cerraron se
// aprobaron en su cierre (mismo criterio que el backend).
export const montoAP = (act) => {
  const cerrados = new Set((act?.closed_providers || []).map(cp => cp.provider_idx));
  return (act?.analysis_items || []).reduce((s, it) => s + asignaciones(it).reduce((t, a) => {
    const p = (it.prices || [])[a.provider_idx];
    return t + (!cerrados.has(a.provider_idx) && p && Number(p.unit_price) > 0 ? Number(p.unit_price) * (Number(a.quantity) || 0) : 0);
  }, 0), 0);
};

// Fix HI (2026-10-21): "debe ser aprobación por cada cierre parcial" (CEN). Monto de un solo proveedor (su columna).
export const montoProveedorAP = (act, idx) => (act?.analysis_items || []).reduce((s, it) => s + asignaciones(it).reduce((t, a) => {
  const p = (it.prices || [])[a.provider_idx];
  return t + (a.provider_idx === idx && p && Number(p.unit_price) > 0 ? Number(p.unit_price) * (Number(a.quantity) || 0) : 0);
}, 0), 0);

// Proveedores abiertos (sin cierre parcial) con cantidad asignada.
export const proveedoresAbiertosAP = (act) => {
  const cerrados = new Set((act?.closed_providers || []).map(cp => cp.provider_idx));
  const idxs = new Set();
  (act?.analysis_items || []).forEach(it => asignaciones(it).forEach(a => {
    if (Number(a.quantity) > 0 && a.provider_idx < (act?.provider_columns || []).length && !cerrados.has(a.provider_idx)) idxs.add(a.provider_idx);
  }));
  return [...idxs].sort((x, y) => x - y);
};

const claveProv = (n) => String(n || "").split(/\s+/).filter(Boolean).join(" ").toLowerCase();
export const aprobacionesProveedorAP = (act, nombre) =>
  ((act?.aprobaciones_ap_prov || []).find(e => claveProv(e?.proveedor) === claveProv(nombre)) || {}).aprobaciones || {};

// Fix HB (2026-10-20): monto adjudicado del APS (Servicios): cantidad × P.U. del proveedor elegido, × tiempo en alquiler
// (mismo criterio que el backend y que «Cerrar APS»).
export const montoAPS = (act) => (act?.analysis_items || []).reduce((s, it) => {
  const p = Number.isInteger(it.selected_idx) && it.selected_idx >= 0 ? (it.prices || [])[it.selected_idx] : null;
  const dias = act?.categoria === "alquiler" ? (Number(p?.dias) || 1) : 1;
  return s + (p && Number(p.unit_price) > 0 ? Number(it.quantity ?? 1) * Number(p.unit_price) * dias : 0);
}, 0);

const enPerfil = (perfil, u) => (perfil.user_ids || []).includes(u.id)
  || (!!perfil.rol && normalizeRole(u.role) === normalizeRole(perfil.rol));

export function estadoAP(cfg, act, calcMonto = montoAP, aprobaciones = undefined) {
  if (!cfg || !(cfg.analisis || []).length || !act) return null;
  const monto = calcMonto(act);
  let desde = 0;
  let tramo = null;
  for (const t of cfg.analisis) {
    if (t.hasta == null || monto <= Number(t.hasta)) { tramo = { ...t, desde }; break; }
    desde = Number(t.hasta);
  }
  if (!tramo) return null;
  const porId = Object.fromEntries((cfg.perfiles || []).map(p => [p.id, p]));
  const requeridos = [...new Set(tramo.perfiles || [])].map(id => porId[id]).filter(p => p && (p.candidatos || []).length);
  const vigentes = Object.fromEntries(Object.entries((aprobaciones === undefined ? act.aprobaciones_ap : aprobaciones) || {}).filter(([k, v]) =>
    requeridos.some(p => p.id === k) && Math.abs((Number(v?.monto) || 0) - monto) < 1));
  return { monto, tramo, requeridos, vigentes, faltan: requeridos.filter(p => !vigentes[p.id]) };
}

// Fix HI (2026-10-21): aprobación del cierre con un proveedor (por el monto de ese proveedor).
export const estadoProveedorAP = (cfg, act, idx) => estadoAP(cfg, act, (a) => montoProveedorAP(a, idx),
  aprobacionesProveedorAP(act, (act?.provider_columns || [])[idx]));

// ¿Cada proveedor abierto con cantidad ya tiene su aprobación? Con eso también se puede «Cerrar AP».
const aprobadoPorProveedores = (cfg, act) => {
  const abiertos = proveedoresAbiertosAP(act);
  return abiertos.length > 0 && abiertos.every(i => {
    const e = estadoProveedorAP(cfg, act, i);
    return !e || !(e.monto > 0) || !e.faltan.length;
  });
};

const textoTramo = (t) => (t.hasta == null
  ? (t.desde ? `más de ${formatPYG(t.desde)}` : "cualquier monto")
  : (t.desde ? `de ${formatPYG(t.desde)} a ${formatPYG(t.hasta)}` : `hasta ${formatPYG(t.hasta)}`));

// Panel del AP (editor y "Ver acta"): monto, tramo, quién aprobó y quién falta.
export function AprobacionAP({ act, cfg, onAprobar, trabajando, servicios = false }) {
  const { user } = useAuth();
  const e = estadoAP(cfg, act, servicios ? montoAPS : montoAP);
  if (!e) return null;
  const uid = user?._id;
  const porProveedor = !servicios && e.faltan.length > 0 && aprobadoPorProveedores(cfg, act);   // Fix HI
  const yaAprobe = Object.values(e.vigentes).some(v => String(v?.by_id) === String(uid));
  const mio = !yaAprobe && !porProveedor && e.monto > 0 ? e.faltan.find(p => (p.candidatos || []).some(c => c.id === uid)) : null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl px-3 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid="ap-aprobacion">
      <div className="flex items-center gap-2 text-xs text-gray-700 flex-wrap">
        <ShieldCheck size={15} className={e.faltan.length ? "text-amber-500" : "text-emerald-600"} />
        <span className="font-semibold">Aprobación</span>
        <span className="text-gray-500" data-testid="ap-aprobacion-tramo">
          {e.monto > 0 ? `${formatPYG(e.monto)} · ${textoTramo(e.tramo)}` : "Asigná proveedores para ver qué aprobación necesita"}
        </span>
      </div>
      {e.monto > 0 && (
        <div className="flex flex-wrap gap-1.5 flex-1">
          {e.requeridos.length === 0 && <span className="text-[11px] text-gray-500">No necesita aprobación.</span>}
          {porProveedor && (
            <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="ap-aprobado-por-proveedor">
              ✓ Cada proveedor tiene su aprobación
            </span>
          )}
          {!porProveedor && e.requeridos.map(p => e.vigentes[p.id] ? (
            <span key={p.id} className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`ap-aprobado-${p.id}`}>
              ✓ {p.nombre}: {e.vigentes[p.id].by_name}
            </span>
          ) : (
            <span key={p.id} className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full" data-testid={`ap-falta-${p.id}`}>
              Falta: {p.nombre}
            </span>
          ))}
        </div>
      )}
      {mio && (
        <button onClick={onAprobar} disabled={trabajando}
          className="flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50 flex-shrink-0"
          data-testid="ap-aprobar-btn">
          {trabajando ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Aprobar ({mio.nombre})
        </button>
      )}
    </div>
  );
}

// Fix HI (2026-10-21): aprobación del cierre con UN proveedor (en el encabezado de su columna).
export function AprobacionProveedorAP({ act, cfg, idx, onAprobar, trabajando }) {
  const { user } = useAuth();
  const e = estadoProveedorAP(cfg, act, idx);
  if (!e || !(e.monto > 0) || !e.requeridos.length) return null;
  const uid = user?._id;
  const yaAprobe = Object.values(e.vigentes).some(v => String(v?.by_id) === String(uid));
  const mio = !yaAprobe ? e.faltan.find(p => (p.candidatos || []).some(c => c.id === uid)) : null;
  return (
    <div className="flex flex-col items-center gap-1 mt-0.5" data-testid={`ap-prov-aprobacion-${idx}`}>
      <span className="text-[9px] font-semibold text-blue-100" data-testid={`ap-prov-monto-${idx}`}>{formatPYG(e.monto)} · {textoTramo(e.tramo)}</span>
      <div className="flex flex-wrap justify-center gap-1">
        {e.requeridos.map(p => e.vigentes[p.id] ? (
          <span key={p.id} className="text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid={`ap-prov-aprobado-${idx}-${p.id}`}>
            ✓ {p.nombre}: {e.vigentes[p.id].by_name}
          </span>
        ) : (
          <span key={p.id} className="text-[9px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded-full" data-testid={`ap-prov-falta-${idx}-${p.id}`}>
            Falta: {p.nombre}
          </span>
        ))}
      </div>
      {mio && (
        <button onClick={onAprobar} disabled={trabajando}
          className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 text-white px-2 py-0.5 rounded-full text-[9px] font-bold disabled:opacity-50 whitespace-nowrap"
          data-testid={`ap-prov-aprobar-${idx}`}>
          {trabajando ? <Loader2 size={9} className="animate-spin" /> : <ShieldCheck size={9} />} Aprobar ({mio.nombre})
        </button>
      )}
    </div>
  );
}

// Etiqueta de la lista de análisis.
export function ChipAprobacionAP({ act, cfg, servicios = false }) {
  const e = estadoAP(cfg, act, servicios ? montoAPS : montoAP);
  if (!e || !e.requeridos.length || !(e.monto > 0)) return null;
  return e.faltan.length && !(!servicios && aprobadoPorProveedores(cfg, act)) ? (
    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid={`ap-aprob-chip-${act._id}`}>
      <ShieldCheck size={10} /> Falta aprobación: {e.faltan.map(p => p.nombre).join(", ")}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={`ap-aprob-chip-${act._id}`}>
      <ShieldCheck size={10} /> Aprobado para cerrar
    </span>
  );
}

// ¿El usuario está en un perfil que aprueba pedidos? Puede aprobar aunque su rol no sea de
// los que aprueban (editar, devolver y rechazar siguen siendo de esos roles).
export function useAprobadorPedidos(servicios = false) {
  const { user } = useAuth();
  const uid = user?._id;
  const [si, setSi] = useState(false);
  useEffect(() => {
    if (!uid) return;
    axios.get(`${API}/settings/rangos-aprobacion${servicios ? "-servicios" : ""}`, { withCredentials: true })   // Fix HB
      .then(({ data }) => setSi((data.pedidos || []).some(id =>
        ((data.perfiles || []).find(p => p.id === id)?.candidatos || []).some(c => c.id === uid))))
      .catch(() => setSi(false));
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  return si;
}

// ── Pestaña de configuración ─────────────────────────────────────────────────
const miles = (n) => (n == null || n === "" ? "" : Number(n).toLocaleString("es-PY", { maximumFractionDigits: 0 }));
const nuevoId = () => `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

function Chips({ perfiles, elegidos, onToggle, disabled, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {perfiles.map(p => {
        const on = elegidos.includes(p.id);
        return (
          <button key={p.id} type="button" disabled={disabled} onClick={() => onToggle(p.id)}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all disabled:cursor-default ${on ? "bg-[#2E6DA4] text-white border-[#2E6DA4]" : "bg-white text-gray-600 border-gray-300 hover:border-[#2E6DA4]/50"}`}
            data-testid={`${testId}-${p.id}`}>
            {on ? "✓ " : ""}{p.nombre || "Sin nombre"}
          </button>
        );
      })}
    </div>
  );
}

export function RangosAprobacionTab({ servicios = false }) {
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const url = `${API}/settings/rangos-aprobacion${servicios ? "-servicios" : ""}`;   // Fix HB

  useEffect(() => {
    axios.get(url, { withCredentials: true })
      .then(({ data }) => setCfg(data))
      .catch(err => setError(err.response?.data?.detail || "No se pudo cargar la configuración"));
  }, [url]);

  if (error) return <p className="text-sm text-gray-500 py-8 text-center">{error}</p>;
  if (!cfg) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400" size={22} /></div>;

  const ro = !cfg.puede_editar;
  const usuarios = cfg.usuarios || [];
  const perfiles = cfg.perfiles || [];
  const tramos = cfg.analisis || [];
  const set = (cambios) => setCfg(c => ({ ...c, ...cambios }));
  const setPerfil = (id, cambios) => set({ perfiles: perfiles.map(p => (p.id === id ? { ...p, ...cambios } : p)) });
  // Una persona elegida en un perfil sale de los demás (aprueba una sola vez).
  const agregarPersona = (id, uid) => set({
    perfiles: perfiles.map(p => (p.id === id
      ? { ...p, user_ids: [...new Set([...(p.user_ids || []), uid])] }
      : { ...p, user_ids: (p.user_ids || []).filter(x => x !== uid) })),
  });
  const quitarPerfil = (id) => set({
    perfiles: perfiles.filter(p => p.id !== id),
    pedidos: (cfg.pedidos || []).filter(x => x !== id),
    analisis: tramos.map(t => ({ ...t, perfiles: (t.perfiles || []).filter(x => x !== id) })),
  });
  const toggle = (lista, id) => (lista.includes(id) ? lista.filter(x => x !== id) : [...lista, id]);
  const setTramo = (i, cambios) => set({ analisis: tramos.map((t, j) => (j === i ? { ...t, ...cambios } : t)) });
  const agregarTramo = () => {
    if (!tramos.length) { set({ analisis: [{ hasta: null, perfiles: perfiles[0] ? [perfiles[0].id] : [] }] }); return; }
    const previo = tramos.length > 1 ? Number(tramos[tramos.length - 2].hasta) || 0 : 0;
    const nuevo = { hasta: previo + 10000000, perfiles: [...(tramos[tramos.length - 1].perfiles || [])] };
    set({ analisis: [...tramos.slice(0, -1), nuevo, tramos[tramos.length - 1]] });
  };
  const quitarTramo = (i) => set({ analisis: tramos.filter((_, j) => j !== i).map((t, j, arr) => (j === arr.length - 1 ? { ...t, hasta: null } : t)) });

  const guardar = async () => {
    if (perfiles.some(p => !(p.nombre || "").trim())) { toast.error("Cada perfil tiene que tener un nombre"); return; }
    for (let i = 0; i < tramos.length - 1; i++) {
      const h = Number(tramos[i].hasta) || 0;
      if (h <= (i ? Number(tramos[i - 1].hasta) || 0 : 0)) { toast.error("Los montos de los tramos tienen que ir de menor a mayor"); return; }
    }
    if (tramos.some(t => !(t.perfiles || []).length)) { toast.error("Cada tramo tiene que tener al menos un perfil"); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(url, {
        perfiles: perfiles.map(({ id, nombre, rol, user_ids }) => ({ id, nombre, rol: rol || "", user_ids: user_ids || [] })),
        pedidos: cfg.pedidos || [],
        analisis: tramos.map((t, i) => ({ hasta: i === tramos.length - 1 ? null : Number(t.hasta) || 0, perfiles: t.perfiles || [] })),
      }, { withCredentials: true });
      setCfg(data);
      toast.success("Rangos de aprobación guardados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const card = "bg-white rounded-2xl border border-gray-200/60 shadow-sm p-4 sm:p-5";
  return (
    <div className="space-y-5 max-w-5xl" data-testid="rangos-aprobacion-tab">
      <div className={card}>
        <h2 className="font-bold text-gray-900 flex items-center gap-2"><ShieldCheck size={18} className="text-[#2E6DA4]" /> Rango p/ Aprobaciones</h2>
        <p className="text-xs text-gray-500 mt-1">{servicios
          ? "Quién aprueba en dos puntos de la compra de servicios: el pedido (para pasar a Pedido a Proveedores) y el Análisis de Presupuesto de Servicio (según el monto adjudicado). Un perfil sin personas no se exige."
          : "Quién aprueba en dos puntos de la compra: el pedido (para pasar a Pedido a Proveedores) y el Análisis de Presupuesto (según el monto adjudicado). Un perfil sin personas no se exige."}</p>
        {servicios && cfg.heredada && <p className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 mt-3" data-testid="rangos-heredada">Servicios todavía no tiene configuración propia: por ahora valen los perfiles y la aprobación de pedidos de Materiales, y no hay tramos. Al guardar, Servicios queda con la suya.</p>}
        {ro && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">Solo lectura: lo cambian Administración y Dirección.</p>}
      </div>

      {/* Perfiles */}
      <div className={card} data-testid="rangos-perfiles">
        <h3 className="text-sm font-bold text-gray-900">Perfiles de aprobación</h3>
        <p className="text-[11px] text-gray-500 mb-3">Aprueban las personas elegidas y, si el perfil tiene un rol, también todos los usuarios con ese rol. Una persona va en un solo perfil.</p>
        <div className="space-y-3">
          {perfiles.map(p => {
            const aprueban = usuarios.filter(u => enPerfil(p, u));
            return (
              <div key={p.id} className="border border-gray-200 rounded-xl p-3 space-y-2" data-testid={`perfil-${p.id}`}>
                <div className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_14rem_auto] gap-2 items-center">
                  <input value={p.nombre} disabled={ro} onChange={e => setPerfil(p.id, { nombre: e.target.value })}
                    placeholder="Nombre del perfil" className="min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold disabled:bg-gray-50"
                    data-testid={`perfil-nombre-${p.id}`} />
                  <select value={p.rol || ""} disabled={ro} onChange={e => setPerfil(p.id, { rol: e.target.value })}
                    className="order-3 sm:order-none col-span-2 sm:col-span-1 min-w-0 border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white disabled:bg-gray-50" data-testid={`perfil-rol-${p.id}`}>
                    <option value="">Sin rol (solo las personas elegidas)</option>
                    {(cfg.roles || []).map(r => <option key={r} value={r}>Rol: {r}</option>)}
                  </select>
                  {!ro && (
                    <button type="button" onClick={() => quitarPerfil(p.id)} title="Quitar perfil"
                      className="order-2 sm:order-none p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`perfil-quitar-${p.id}`}>
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(p.user_ids || []).map(uid => {
                    const u = usuarios.find(x => x.id === uid);
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 text-[11px] bg-[#EBF3FB] text-[#1A4B7A] border border-[#2E6DA4]/20 rounded-full pl-2 pr-1 py-0.5">
                        {u ? u.name : "Usuario inactivo"}
                        {!ro && <button type="button" onClick={() => setPerfil(p.id, { user_ids: p.user_ids.filter(x => x !== uid) })} className="p-0.5 min-h-0 hover:text-red-600" title="Quitar"><X size={11} /></button>}
                      </span>
                    );
                  })}
                  {!ro && (
                    <div className="w-full sm:w-64">
                      <SelectBuscable value="" onChange={uid => uid && agregarPersona(p.id, uid)}
                        options={usuarios.filter(u => !(p.user_ids || []).includes(u.id)).map(u => ({ value: u.id, label: `${u.name} · ${u.role}` }))}
                        vacio="+ Agregar persona" placeholderBuscar="Buscar persona..."
                        className="w-full border border-dashed border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white" testId={`perfil-persona-${p.id}`} />
                    </div>
                  )}
                </div>
                <p className={`text-[11px] ${aprueban.length ? "text-gray-500" : "text-amber-700"}`} data-testid={`perfil-aprueban-${p.id}`}>
                  {aprueban.length ? `Aprueban: ${aprueban.map(u => u.name).join(", ")}` : "Sin personas: este perfil no se exige."}
                </p>
              </div>
            );
          })}
        </div>
        {!ro && (
          <button type="button" onClick={() => set({ perfiles: [...perfiles, { id: nuevoId(), nombre: "", rol: "", user_ids: [] }] })}
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] border border-dashed border-[#2E6DA4]/50 rounded-lg px-3 py-2 hover:bg-blue-50" data-testid="perfil-agregar">
            <Plus size={13} /> Agregar perfil
          </button>
        )}
      </div>

      {/* Pedidos */}
      <div className={card} data-testid="rangos-pedidos">
        <h3 className="text-sm font-bold text-gray-900">{servicios ? "Aprobaciones — pedidos de servicios" : "Aprobaciones — pedidos de materiales"}</h3>
        <p className="text-[11px] text-gray-500 mb-3">Para pasar a Pedido a Proveedores tienen que aprobar todos los perfiles marcados. Sin ninguno marcado, el pedido se aprueba con un solo aprobado.</p>
        <Chips perfiles={perfiles} elegidos={cfg.pedidos || []} disabled={ro} testId="pedidos-perfil"
          onToggle={id => set({ pedidos: toggle(cfg.pedidos || [], id) })} />
      </div>

      {/* Análisis de Presupuesto */}
      <div className={card} data-testid="rangos-analisis">
        <h3 className="text-sm font-bold text-gray-900">{servicios ? "Análisis de Presupuesto de Servicio — por monto adjudicado" : "Análisis de Presupuesto — por monto adjudicado"}</h3>
        <p className="text-[11px] text-gray-500 mb-3">{servicios ? "Para cerrar el análisis tienen" : "Para cerrar el análisis (o un proveedor del análisis) tienen"} que aprobar los perfiles del tramo de su monto. Si el monto cambia después, se aprueba de nuevo. Sin tramos, el análisis se cierra sin aprobaciones.</p>
        <div className="space-y-2">
          {tramos.map((t, i) => {
            const ultimo = i === tramos.length - 1;
            const desde = i ? Number(tramos[i - 1].hasta) || 0 : 0;
            return (
              <div key={i} className="border border-gray-200 rounded-xl p-3 flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-3" data-testid={`tramo-${i}`}>
                <div className="flex items-center gap-2 text-xs text-gray-700 flex-wrap lg:w-80 flex-shrink-0">
                  <span className="text-gray-500">{desde ? `Más de ${formatPYG(desde)}` : "Desde Gs. 0"}</span>
                  {ultimo ? (
                    <span className="font-semibold">· sin límite</span>
                  ) : (
                    <label className="flex items-center gap-1.5">
                      <span className="text-gray-500">hasta Gs.</span>
                      <input inputMode="numeric" value={miles(t.hasta)} disabled={ro}
                        onChange={e => setTramo(i, { hasta: Number(e.target.value.replace(/\D/g, "")) || 0 })}
                        className="w-32 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-right font-semibold disabled:bg-gray-50"
                        data-testid={`tramo-hasta-${i}`} />
                    </label>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Chips perfiles={perfiles} elegidos={t.perfiles || []} disabled={ro} testId={`tramo-${i}-perfil`}
                    onToggle={id => setTramo(i, { perfiles: toggle(t.perfiles || [], id) })} />
                </div>
                {!ro && (
                  <button type="button" onClick={() => quitarTramo(i)} title="Quitar tramo"
                    className="self-start lg:self-center p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" data-testid={`tramo-quitar-${i}`}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            );
          })}
          {!tramos.length && <p className="text-xs text-gray-500 italic">Sin tramos: el análisis se cierra sin aprobaciones.</p>}
        </div>
        {!ro && (
          <button type="button" onClick={agregarTramo}
            className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-[#2E6DA4] border border-dashed border-[#2E6DA4]/50 rounded-lg px-3 py-2 hover:bg-blue-50" data-testid="tramo-agregar">
            <Plus size={13} /> Agregar tramo
          </button>
        )}
      </div>

      {!ro && (
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={guardar} disabled={saving}
            className="px-5 py-2.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2" data-testid="rangos-guardar">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <ShieldCheck size={15} />} Guardar
          </button>
          {cfg.updated_at && <span className="text-[11px] text-gray-400">Última actualización: {new Date(cfg.updated_at).toLocaleString("es-PY")}{cfg.updated_by ? ` · ${cfg.updated_by}` : ""}</span>}
        </div>
      )}
    </div>
  );
}
